/**
 * PeoplesDock — Tenant Signup (Production Ready v6)
 *
 * FIX: Free email domain validation moved to validateStep1()
 *      so it fires when user clicks "Continue to Plan Selection"
 *      — BEFORE Razorpay ever opens. No wasted payments.
 *
 * All other logic unchanged.
 */
import React, { useState, useEffect } from 'react';
import {
    Container, Row, Col, Card, Button, Form,
    InputGroup, Spinner, Badge, Alert, Modal,
} from 'react-bootstrap';
import {
    Check, Building2, Mail, Phone, ShieldCheck,
    Tag, ChevronRight, ChevronLeft, CheckCircle, Eye, EyeOff,
    Users, Zap, ArrowRight, Gift,
} from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import { AuthContext } from '../../context/AuthContext';
// ── Free/personal email domains — blocked for company signups ─────────────────
const FREE_EMAIL_DOMAINS = new Set([
    'gmail.com','yahoo.com','hotmail.com','outlook.com','live.com',
    'icloud.com','me.com','aol.com','protonmail.com','zohomail.com',
    'yandex.com','mail.com','inbox.com','rediffmail.com','yahoo.in',
    'yahoo.co.in','hotmail.in','outlook.in','googlemail.com',
]);
// ── Normalize plan from API ───────────────────────────────────────────────────
const normalizePlan = (p) => {
    const monthly = p.monthly_price != null ? parseFloat(p.monthly_price)
                  : p.monthly        != null ? parseFloat(p.monthly)
                  : null;
    const yearly  = p.yearly_price  != null ? parseFloat(p.yearly_price)
                  : p.yearly        != null ? parseFloat(p.yearly)
                  : null;
    return {
        ...p,
        monthly,
        yearly,
        monthly_price: monthly,
        yearly_price:  yearly,
        is_enterprise: p.plan_code === 'enterprise'
                    || p.name?.toLowerCase() === 'enterprise'
                    || parseInt(p.max_employees) >= 9999,
        popular: p.popular
              ?? (p.plan_code === 'growth')
              ?? (p.name?.toLowerCase() === 'growth'),
        features: Array.isArray(p.features) ? p.features : [],
    };
};
const INDUSTRIES    = ['Technology','Healthcare','Manufacturing','Retail','Education',
    'Finance','Real Estate','Hospitality','Logistics','Consulting','Media','Other'];
const COMPANY_SIZES = ['1–10','11–50','51–200','201–500','500+'];
const PLAN_COLORS   = ['#4f46e5','#7c3aed','#0891b2','#059669','#dc2626'];
// ── Phone validation + auto-format — generic international support ───────────
// Accepts either a bare national number (7-15 digits, no country code —
// e.g. a plain 10-digit Indian mobile typed without +91) or a full
// international number ('+' followed by 7-15 digits total, covering the
// E.164 standard's maximum length used by virtually every real country
// code + national number combination worldwide — +91, +1, +44, +86,
// +971, etc. all fit comfortably within this range). This is deliberately
// less strict than per-country rules (e.g. it doesn't check that an
// Indian mobile starts with 6-9) in exchange for working correctly for
// any country without needing a country-code lookup table or a dropdown.
const isValidPhone = (raw) => {
    const cleaned = (raw || '').replace(/[\s\-()]/g, '');
    return /^\+?[0-9]{7,15}$/.test(cleaned);
};
// Formats AS THE USER TYPES into a readable spaced-out display — purely
// cosmetic, the underlying digits (and leading + if present) are what
// get validated and sent to the backend. Since there's no country-code
// lookup table, the grouping is a reasonable generic approximation
// rather than a per-country-perfect format: international numbers show
// the country code (guessed as 1 digit for the common +1/+7 codes, 2
// digits otherwise) separated from the rest, which is grouped in
// readable chunks; domestic 10-digit numbers keep the familiar
// "XXXXX XXXXX" split used for Indian mobiles.
const formatPhoneInput = (raw) => {
    const hasPlus = (raw || '').trim().startsWith('+');
    let digits = (raw || '').replace(/\D/g, '').slice(0, 15);
    if (hasPlus) {
        const ccLen  = /^[17]/.test(digits) ? 1 : 2; // +1/+7 are 1-digit codes, most others are 2
        const cc     = digits.slice(0, ccLen);
        const rest   = digits.slice(ccLen);
        const grouped = rest.length > 5
            ? `${rest.slice(0, 5)} ${rest.slice(5)}`
            : rest;
        return cc ? `+${cc} ${grouped}`.trim() : '+';
    }
    if (digits.length > 5) return `${digits.slice(0, 5)} ${digits.slice(5)}`;
    return digits;
};
// ══════════════════════════════════════════════════════════════════════════════
const TenantSignup = () => {
    const navigate  = useNavigate();
    const location  = useLocation();
    const { login } = React.useContext(AuthContext);
    const [step, setStep] = useState(1);
    const [plans,       setPlans]       = useState([]);
    const [loadingInit, setLoadingInit] = useState(true);
    const params = new URLSearchParams(location.search);
    const [form, setForm] = useState({
        first_name:'', last_name:'', email: params.get('email') || '',
        password:'', confirm_password:'',
        company_name:'', company_size:'', phone:'', industry:'',
    });
    const [showPw,  setShowPw]  = useState(false);
    const [showCpw, setShowCpw] = useState(false);
    const [errors,  setErrors]  = useState({});
    const [selectedPlanId, setSelectedPlanId] = useState(null);
    const [isYearly,       setIsYearly]       = useState(false);
    const [promoCode,      setPromoCode]       = useState('');
    const [promoData,      setPromoData]       = useState(null);
    const [promoLoading,   setPromoLoading]    = useState(false);
    const [promoError,     setPromoError]      = useState('');
    const [submitting,     setSubmitting]       = useState(false);
    const [gatewayCfg, setGatewayCfg] = useState({ gateway:'razorpay', razorpay_key_id:null });
    const [selectedGW, setSelectedGW] = useState(null);
    const [step2Sub,   setStep2Sub]   = useState('plan');
    const [termsAccepted,  setTermsAccepted]  = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [termsScrolled,  setTermsScrolled]  = useState(false);
    // ── Load plans + gateway config ───────────────────────────────────────────
    useEffect(() => {
        axiosInstance.get('/superadmin/public/gateway-config')
            .then(r => {
                const raw = r.data.data || {};
                const cfg = {
                    gateway:          raw.active_gateway || raw.gateway || 'razorpay',
                    razorpay_enabled: raw.razorpay_enabled === true || raw.razorpay_enabled === '1' || raw.razorpay_enabled === 1,
                    stripe_enabled:   raw.stripe_enabled   === true || raw.stripe_enabled   === '1' || raw.stripe_enabled   === 1,
                };
                setGatewayCfg(cfg);
                const defaultGW = cfg.razorpay_enabled ? (cfg.gateway || 'razorpay') : 'stripe';
                setSelectedGW(defaultGW);
            }).catch(() => {
                setGatewayCfg({ gateway:'razorpay', razorpay_enabled:true, stripe_enabled:false });
                setSelectedGW('razorpay');
            });
        axiosInstance.get('/public/plans')
            .then(res => {
                const raw  = res.data.data || [];
                const list = raw.map(normalizePlan);
                setPlans(list);
                const growth = list.find(p => p.plan_code === 'growth' || p.popular)
                    || list[Math.min(1, list.length - 1)];
                setSelectedPlanId(growth?.id ?? list[0]?.id);
            })
            .catch(err => console.error('[TenantSignup] Failed to load plans:', err.message))
            .finally(() => setLoadingInit(false));
    }, []);
    const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));
    // ── Abandoned signup tracking ─────────────────────────────────────────────
    // Fire-and-forget capture so HR/Sales can follow up with people who
    // started signing up but didn't complete. Never blocks or alerts the
    // person signing up — failures are silently swallowed. See
    // backend/src/modules/signup-intents/signup-intents.routes.js.
    const trackSignupIntent = (step_reached) => {
        axiosInstance.post('/public/signup-intent', {
            first_name: form.first_name, last_name: form.last_name,
            email: form.email, phone: form.phone,
            company_name: form.company_name, company_size: form.company_size,
            industry: form.industry,
            selected_plan_name: selectedPlan?.name,
            billing_cycle: isYearly ? 'yearly' : 'monthly',
            promo_code: promoData?.code || promoCode || null,
            step_reached,
        }).catch(() => {});
    };
    // Debounced capture of Step 1 partial fill — catches people who type
    // their email/company name but close the tab before clicking Continue.
    // Fires 1.5s after typing stops, only once email looks roughly valid
    // or a company name has been entered, so we don't hammer the endpoint
    // on every keystroke of an obviously-incomplete form.
    useEffect(() => {
        const emailLooksValid = /^[^@]+@[^@]+\.[^@]+$/.test(form.email.trim());
        if (!emailLooksValid && !form.company_name.trim()) return;
        const t = setTimeout(() => {
            trackSignupIntent('step1_started');
        }, 1500);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.email, form.company_name]);
    const [checkingEmail, setCheckingEmail] = useState(false);
    // ── Step 1 validation — includes free email domain check + live
    // duplicate-email check + phone format check ─────────────────────────────
    // FIX: Domain check runs HERE, before user can proceed to Step 2 (plan selection).
    //      Previously this only ran on the backend after payment — now it blocks
    //      the user at Step 1 so no payment is ever attempted with a personal email.
    // FIX (this version): registerTenant() already rejected duplicate emails
    //      with a 409 — but only at FINAL submission, after Step 2 and possibly
    //      after a real payment. Now checked here too, at Step 1, so the person
    //      finds out immediately instead of after doing all that extra work.
    const validateStep1 = async () => {
        const e = {};
        if (!form.first_name.trim())  e.first_name      = 'Required';
        if (!form.last_name.trim())   e.last_name       = 'Required';
        const emailTrimmed = form.email.trim().toLowerCase();
        if (!emailTrimmed || !/^[^@]+@[^@]+\.[^@]+$/.test(emailTrimmed)) {
            e.email = 'Valid email required';
        } else {
            // FIX: Block free/personal email domains
            const domain = emailTrimmed.split('@')[1] || '';
            if (FREE_EMAIL_DOMAINS.has(domain)) {
                e.email = `Please use your company email. Free providers like ${domain} are not accepted.`;
            }
        }
        if (form.password.length < 8) e.password        = 'Minimum 8 characters';
        if (form.password !== form.confirm_password)
            e.confirm_password = 'Passwords do not match';
        if (!form.company_name.trim()) e.company_name   = 'Required';
        if (!form.company_size)        e.company_size   = 'Required';
        if (!form.phone.trim()) {
            e.phone = 'Phone number is required';
        } else if (!isValidPhone(form.phone)) {
            e.phone = 'Enter a valid 10-digit mobile number (with optional +91)';
        }
        if (!termsAccepted) {
            e.terms = 'You must accept the Terms & Conditions to continue';
        }
        // Only hit the backend for the duplicate-email check if everything
        // else about the email already looks valid — no point checking an
        // obviously malformed or free-domain address.
        if (!e.email && emailTrimmed) {
            setCheckingEmail(true);
            try {
                const res = await axiosInstance.get('/companies/check-email', {
                    params: { email: emailTrimmed },
                });
                if (res.data?.data?.available === false) {
                    e.email = 'An account with this email already exists. Please log in or use a different email.';
                }
            } catch {
                // Fail open — if the check itself is unreachable, don't block
                // the person; registerTenant() re-checks this at submission anyway.
            } finally {
                setCheckingEmail(false);
            }
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };
    // ── Promo validation ──────────────────────────────────────────────────────
    const applyPromo = async () => {
        if (!promoCode.trim()) return;
        setPromoLoading(true); setPromoError(''); setPromoData(null);
        try {
            const res = await axiosInstance.post('/public/validate-promo',
                { code: promoCode.trim().toUpperCase() });
            setPromoData(res.data.data);
            toast.success('Promo code applied!');
        } catch (err) {
            setPromoError(err.response?.data?.message || 'Invalid promo code');
        } finally { setPromoLoading(false); }
    };
    // ── Pricing calculations ──────────────────────────────────────────────────
    const selectedPlan   = plans.find(p => p.id === selectedPlanId);
    const displayPrice   = selectedPlan
        ? (isYearly ? selectedPlan.yearly : selectedPlan.monthly) ?? null : null;
    const perMonthYearly = (displayPrice && isYearly) ? Math.round(displayPrice / 12) : null;
    const basePrice      = displayPrice ?? 0;
    const getDiscounted = (base) => {
        if (!promoData || !base) return base;
        if (promoData.discount_type === 'percentage')
            return Math.max(0, Math.round(base * (1 - promoData.discount_value / 100)));
        return Math.max(0, base - promoData.discount_value);
    };
    const finalPrice  = getDiscounted(basePrice);
    const savedAmount = basePrice - finalPrice;
    const trialDays   = promoData?.trial_days > 0 ? promoData.trial_days : 0;
    const isEnterprise = selectedPlan?.plan_code === 'enterprise'
        || selectedPlan?.name?.toLowerCase() === 'enterprise'
        || (selectedPlan?.max_employees && parseInt(selectedPlan.max_employees) >= 9999);
    const skipPayment = !isEnterprise && (finalPrice === 0 || trialDays > 0);
    const promoMessage = promoData ? (() => {
        const parts = [];
        if (trialDays > 0) parts.push(`🎉 ${trialDays}-day free trial activated!`);
        else if (savedAmount > 0)
            parts.push(`🎉 ₹${savedAmount.toLocaleString('en-IN')} saved!`);
        if (!isEnterprise && finalPrice === 0) parts.push('No payment needed!');
        return parts.join(' — ') || promoData.description || 'Promo applied!';
    })() : '';
    // ── Create free account ───────────────────────────────────────────────────
    const createFreeAccount = async () => {
        setSubmitting(true);
        try {
            const res = await axiosInstance.post('/auth/tenant/register', {
                ...form,
                plan_id:       selectedPlanId,
                billing_cycle: isYearly ? 'yearly' : 'monthly',
                gateway:       'free',
                amount:        0,
                promo_code:    promoData?.code || promoCode || null,
                trial_days:    trialDays || 14,
            });
            toast.success('Account created! Check your email to verify.');
            if (res.data.data?.token) { login(res.data.data.token); navigate('/dashboard'); }
            else navigate('/login');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Registration failed');
        } finally { setSubmitting(false); }
    };
    // ── Stripe payment ────────────────────────────────────────────────────────
    const payWithStripe = async () => {
        setSubmitting(true);
        trackSignupIntent('payment_initiated');
        try {
            const res = await axiosInstance.post('/payments/stripe/create-session', {
                plan_id:       selectedPlanId,
                billing_cycle: isYearly ? 'yearly' : 'monthly',
                success_url:   `${window.location.origin}/register?success=1`,
                cancel_url:    `${window.location.origin}/register`,
            });
            window.location.href = res.data.data.checkout_url;
        } catch (err) {
            toast.error(err.response?.data?.message || 'Stripe payment failed. Try again.');
            setSubmitting(false);
        }
    };
    // ── Razorpay payment ──────────────────────────────────────────────────────
    const payWithRazorpay = async () => {
        if (!window.Razorpay) {
            toast.error('Payment gateway not loaded. Refresh and try again.'); return;
        }
        setSubmitting(true);
        try {
            const orderRes = await axiosInstance.post('/payments/razorpay/create-order', {
                amount: Math.round(finalPrice * 100), currency:'INR',
                plan_id: selectedPlanId, promo_code: promoData?.code || null,
            });
            const { order_id, key } = orderRes.data.data;
            const opts = {
                key, amount: Math.round(finalPrice * 100), currency:'INR',
                name:'PeoplesDock',
                description:`${selectedPlan?.name} Plan`,
                order_id,
                prefill:{ name:`${form.first_name} ${form.last_name}`, email:form.email, contact:form.phone },
                handler: async (rz) => {
                    try {
                        const reg = await axiosInstance.post('/auth/tenant/register', {
                            ...form,
                            plan_id:       selectedPlanId,
                            billing_cycle: isYearly ? 'yearly' : 'monthly',
                            gateway:       'razorpay',
                            amount:        finalPrice,
                            promo_code:    promoData?.code || null,
                            razorpay_payment_id: rz.razorpay_payment_id,
                            razorpay_order_id:   rz.razorpay_order_id,
                            razorpay_signature:  rz.razorpay_signature,
                        });
                        toast.success('Account created!');
                        if (reg.data.data?.token) { login(reg.data.data.token); navigate('/dashboard'); }
                        else navigate('/login');
                    } catch (err) {
                        toast.error(err.response?.data?.message || 'Registration failed');
                    }
                },
                modal:{ ondismiss: () => { setSubmitting(false); trackSignupIntent('payment_abandoned'); } },
                theme:{ color:'#4f46e5' },
            };
            trackSignupIntent('payment_initiated');
            new window.Razorpay(opts).open();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Payment failed');
            setSubmitting(false);
        }
    };
    // ── Password strength ─────────────────────────────────────────────────────
    const pwStrength = !form.password ? 0
        : form.password.length >= 12 && /[A-Z]/.test(form.password) && /[0-9]/.test(form.password) && /[^A-Za-z0-9]/.test(form.password) ? 4
        : form.password.length >= 10 && /[A-Z]/.test(form.password) ? 3
        : form.password.length >= 8 ? 2 : 1;
    const pwColors = ['','#ef4444','#f59e0b','#3b82f6','#059669'];
    const pwLabels = ['','Weak','Fair','Good','Strong'];
    const planColor = (plan, idx) => plan.color || PLAN_COLORS[idx % PLAN_COLORS.length];
    // ════════════════════════════════════════════════════════════════════════
    return (
        <div style={{minHeight:'100vh', background:'linear-gradient(135deg,#f0f4ff 0%,#faf5ff 50%,#f0fdf4 100%)'}}>
            {/* Top bar */}
            <div style={{
                background:'rgba(255,255,255,0.9)', backdropFilter:'blur(12px)',
                borderBottom:'1px solid rgba(79,70,229,0.1)',
                padding:'12px 24px', display:'flex', justifyContent:'space-between',
                alignItems:'center', position:'sticky', top:0, zIndex:100,
            }}>
                <Link to="/" style={{textDecoration:'none', display:'flex', alignItems:'center', gap:10}}>
                    <img src="/logo.png" alt="PeoplesDock"
                        style={{height:70, width:'auto', objectFit:'contain'}}
                        onError={e=>{e.target.style.display='none';}}/>
                </Link>
                <span className="text-muted small">
                    Already have an account?{' '}
                    <Link to="/login" className="fw-bold text-decoration-none" style={{color:'#4f46e5'}}>Log in</Link>
                </span>
            </div>
            <Container style={{maxWidth: step === 2 ? 1200 : 680, padding:'32px 16px'}}>
                {/* Step indicator */}
                <div className="d-flex justify-content-center align-items-center gap-3 mb-5">
                    {['Account Details','Choose a Plan'].map((label, i) => {
                        const s=i+1, done=step>s, active=step===s;
                        return (
                            <React.Fragment key={s}>
                                <div className="d-flex align-items-center gap-2">
                                    <div style={{
                                        width:34, height:34, borderRadius:'50%',
                                        background: done?'#059669':active?'#4f46e5':'#e5e7eb',
                                        color:'#fff', display:'flex', alignItems:'center',
                                        justifyContent:'center', fontWeight:700, fontSize:14,
                                        transition:'all 0.3s',
                                    }}>
                                        {done ? <CheckCircle size={16}/> : s}
                                    </div>
                                    <span style={{
                                        fontSize:14, fontWeight:active?700:500,
                                        color: active?'#4f46e5':done?'#059669':'#9ca3af',
                                    }}>{label}</span>
                                </div>
                                {i<1 && <div style={{height:2,width:60,borderRadius:2,
                                    background:step>1?'#059669':'#e5e7eb',transition:'background 0.3s'}}/>}
                            </React.Fragment>
                        );
                    })}
                </div>
                {/* ─────────────────── STEP 1 ─────────────────── */}
                {step === 1 && (
                    <Card className="border-0 shadow-sm" style={{borderRadius:20}}>
                        <Card.Body className="p-4 p-md-5">
                            <h4 className="fw-bold mb-1">Create your account</h4>
                            <p className="text-muted small mb-4">
                                Set up your HR platform in 2 minutes. No credit card required for trial.
                            </p>
                            <div className="fw-bold small text-muted text-uppercase mb-3" style={{letterSpacing:1}}>
                                👤 Your Details
                            </div>
                            <Row className="g-3 mb-4">
                                <Col xs={6}>
                                    <Form.Label className="small fw-bold">First Name *</Form.Label>
                                    <Form.Control value={form.first_name}
                                        onChange={e=>setF('first_name',e.target.value)}
                                        placeholder="Megha" isInvalid={!!errors.first_name}
                                        style={{borderRadius:10}}/>
                                    <Form.Control.Feedback type="invalid">{errors.first_name}</Form.Control.Feedback>
                                </Col>
                                <Col xs={6}>
                                    <Form.Label className="small fw-bold">Last Name *</Form.Label>
                                    <Form.Control value={form.last_name}
                                        onChange={e=>setF('last_name',e.target.value)}
                                        placeholder="Chowdary" isInvalid={!!errors.last_name}
                                        style={{borderRadius:10}}/>
                                    <Form.Control.Feedback type="invalid">{errors.last_name}</Form.Control.Feedback>
                                </Col>
                                <Col xs={12}>
                                    <Form.Label className="small fw-bold">Work Email *</Form.Label>
                                    <InputGroup>
                                        <InputGroup.Text style={{borderRadius:'10px 0 0 10px'}}>
                                            <Mail size={14} color="#94a3b8"/>
                                        </InputGroup.Text>
                                        <Form.Control value={form.email}
                                            onChange={e=>setF('email',e.target.value)}
                                            placeholder="you@company.com" type="email"
                                            isInvalid={!!errors.email}
                                            style={{borderRadius:'0 10px 10px 0'}}/>
                                        <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                                    </InputGroup>
                                    {/* FIX: Show hint below email field so user knows upfront */}
                                    <div className="text-muted mt-1" style={{fontSize:11}}>
                                        💼 Use your company email — personal emails (Gmail, Yahoo, etc.) are not accepted.
                                    </div>
                                </Col>
                                <Col xs={6}>
                                    <Form.Label className="small fw-bold">Password *</Form.Label>
                                    <InputGroup>
                                        <Form.Control type={showPw?'text':'password'}
                                            value={form.password}
                                            onChange={e=>setF('password',e.target.value)}
                                            placeholder="Min. 8 characters"
                                            isInvalid={!!errors.password}
                                            style={{borderRadius:'10px 0 0 10px'}}/>
                                        <Button variant="outline-secondary"
                                            style={{borderRadius:'0 10px 10px 0'}}
                                            onClick={()=>setShowPw(p=>!p)}>
                                            {showPw?<EyeOff size={14}/>:<Eye size={14}/>}
                                        </Button>
                                        <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
                                    </InputGroup>
                                    {form.password && (
                                        <div className="mt-1">
                                            <div className="d-flex gap-1">
                                                {[1,2,3,4].map(i=>(
                                                    <div key={i} style={{flex:1,height:3,borderRadius:2,
                                                        background:i<=pwStrength?pwColors[pwStrength]:'#e5e7eb',
                                                        transition:'background 0.2s'}}/>
                                                ))}
                                            </div>
                                            <span style={{fontSize:10,color:pwColors[pwStrength],fontWeight:600}}>
                                                {pwLabels[pwStrength]}
                                            </span>
                                        </div>
                                    )}
                                </Col>
                                <Col xs={6}>
                                    <Form.Label className="small fw-bold">Confirm Password *</Form.Label>
                                    <InputGroup>
                                        <Form.Control type={showCpw?'text':'password'}
                                            value={form.confirm_password}
                                            onChange={e=>setF('confirm_password',e.target.value)}
                                            placeholder="Repeat password"
                                            isInvalid={!!errors.confirm_password}
                                            style={{borderRadius:'10px 0 0 10px'}}/>
                                        <Button variant="outline-secondary"
                                            style={{borderRadius:'0 10px 10px 0'}}
                                            onClick={()=>setShowCpw(p=>!p)}>
                                            {showCpw?<EyeOff size={14}/>:<Eye size={14}/>}
                                        </Button>
                                        <Form.Control.Feedback type="invalid">{errors.confirm_password}</Form.Control.Feedback>
                                    </InputGroup>
                                </Col>
                                <Col xs={12}>
                                    <Form.Label className="small fw-bold">Phone *</Form.Label>
                                    <InputGroup>
                                        <InputGroup.Text style={{borderRadius:'10px 0 0 10px'}}>
                                            <Phone size={14} color="#94a3b8"/>
                                        </InputGroup.Text>
                                        <Form.Control value={form.phone}
                                            onChange={e=>setF('phone', formatPhoneInput(e.target.value))}
                                            placeholder="+91 96118 92030"
                                            isInvalid={!!errors.phone}
                                            style={{borderRadius:'0 10px 10px 0'}}/>
                                        <Form.Control.Feedback type="invalid">{errors.phone}</Form.Control.Feedback>
                                    </InputGroup>
                                </Col>
                            </Row>
                            <div className="fw-bold small text-muted text-uppercase mb-3" style={{letterSpacing:1}}>
                                🏢 Company Details
                            </div>
                            <Row className="g-3 mb-4">
                                <Col xs={12}>
                                    <Form.Label className="small fw-bold">Company Name *</Form.Label>
                                    <InputGroup>
                                        <InputGroup.Text style={{borderRadius:'10px 0 0 10px'}}>
                                            <Building2 size={14} color="#94a3b8"/>
                                        </InputGroup.Text>
                                        <Form.Control value={form.company_name}
                                            onChange={e=>setF('company_name',e.target.value)}
                                            placeholder="AMC Technologies Pvt. Ltd."
                                            isInvalid={!!errors.company_name}
                                            style={{borderRadius:'0 10px 10px 0'}}/>
                                        <Form.Control.Feedback type="invalid">{errors.company_name}</Form.Control.Feedback>
                                    </InputGroup>
                                </Col>
                                <Col xs={6}>
                                    <Form.Label className="small fw-bold">Team Size *</Form.Label>
                                    <Form.Select value={form.company_size}
                                        onChange={e=>setF('company_size',e.target.value)}
                                        isInvalid={!!errors.company_size}
                                        style={{borderRadius:10}}>
                                        <option value="">Select size…</option>
                                        {COMPANY_SIZES.map(s=><option key={s} value={s}>{s} employees</option>)}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">{errors.company_size}</Form.Control.Feedback>
                                </Col>
                                <Col xs={6}>
                                    <Form.Label className="small fw-bold">Industry</Form.Label>
                                    <Form.Select value={form.industry}
                                        onChange={e=>setF('industry',e.target.value)}
                                        style={{borderRadius:10}}>
                                        <option value="">Select industry…</option>
                                        {INDUSTRIES.map(ind=><option key={ind} value={ind}>{ind}</option>)}
                                    </Form.Select>
                                </Col>
                            </Row>
                            {/* Terms & Conditions checkbox */}
                            <div className="mb-3 mt-2">
                                <div className="d-flex align-items-start gap-2"
                                    style={{
                                        padding: '12px 14px',
                                        borderRadius: 10,
                                        background: errors.terms ? '#fff5f5' : '#f8fafc',
                                        border: `1px solid ${errors.terms ? '#fca5a5' : '#e5e7eb'}`,
                                        transition: 'all 0.2s',
                                    }}>
                                    <Form.Check
                                        type="checkbox"
                                        id="terms-checkbox"
                                        checked={termsAccepted}
                                        onChange={e => {
                                            if (e.target.checked && !termsScrolled) {
                                                // Must open and scroll modal before accepting
                                                setShowTermsModal(true);
                                            } else {
                                                setTermsAccepted(e.target.checked);
                                                if (errors.terms) setErrors(p => ({ ...p, terms: '' }));
                                            }
                                        }}
                                        style={{ marginTop: 2, flexShrink: 0 }}
                                        isInvalid={!!errors.terms}
                                    />
                                    <label htmlFor="terms-checkbox"
                                        style={{ fontSize: 13, cursor: 'pointer', lineHeight: 1.5 }}>
                                        I have read and agree to the{' '}
                                        <span
                                            onClick={e => { e.preventDefault(); setShowTermsModal(true); }}
                                            style={{
                                                color: '#4f46e5', fontWeight: 700,
                                                textDecoration: 'underline', cursor: 'pointer',
                                            }}>
                                            Terms &amp; Conditions
                                        </span>
                                        {' '}and{' '}
                                        <span
                                            onClick={e => { e.preventDefault(); setShowTermsModal(true); }}
                                            style={{
                                                color: '#4f46e5', fontWeight: 700,
                                                textDecoration: 'underline', cursor: 'pointer',
                                            }}>
                                            Privacy Policy
                                        </span>
                                        {' '}<span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                </div>
                                {errors.terms && (
                                    <div className="text-danger mt-1" style={{ fontSize: 12 }}>
                                        ⚠ {errors.terms}
                                    </div>
                                )}
                            </div>
                            {/* Continue button */}
                            <Button variant="primary" className="w-100 py-3 fw-bold rounded-pill"
                                style={{
                                    background: 'linear-gradient(135deg,#4f46e5,#7c3aed)',
                                    border: 'none', fontSize: 16,
                                    opacity: termsAccepted ? 1 : 0.7,
                                }}
                                disabled={checkingEmail}
                                onClick={async () => {
                                    const ok = await validateStep1();
                                    if (ok) { setStep(2); trackSignupIntent('step2_reached'); }
                                }}>
                                {checkingEmail ? (
                                    <><Spinner size="sm" className="me-2"/>Checking…</>
                                ) : (
                                    <>Continue to Plan Selection <ChevronRight size={18} className="ms-2"/></>
                                )}
                            </Button>
                            <div className="d-flex align-items-center justify-content-center gap-4 mt-4 flex-wrap">
                                {[{icon:ShieldCheck,text:'256-bit SSL'},{icon:Zap,text:'2-min setup'},{icon:Gift,text:'14-day trial'}]
                                    .map(({icon:Icon,text},i)=>(
                                    <div key={i} className="d-flex align-items-center gap-1 text-muted" style={{fontSize:12}}>
                                        <Icon size={13} color="#4f46e5"/><span>{text}</span>
                                    </div>
                                ))}
                            </div>
                        </Card.Body>
                    </Card>
                )}
                {/* ─────────────────── STEP 2 ─────────────────── */}
                {step === 2 && (
                    <div>
                        {/* Header + billing toggle */}
                        <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
                            <div>
                                <h4 className="fw-bold mb-1">Choose your plan</h4>
                                <p className="text-muted small mb-0">All plans include a 14-day free trial. Cancel anytime.</p>
                            </div>
                            <div className="d-flex align-items-center gap-2 bg-white rounded-pill px-3 py-2 shadow-sm">
                                <span className="small fw-medium" style={{color:!isYearly?'#4f46e5':'#9ca3af'}}>Monthly</span>
                                <Form.Check type="switch" id="billing-switch"
                                    checked={isYearly} onChange={()=>setIsYearly(v=>!v)} className="mb-0"/>
                                <span className="small fw-medium" style={{color:isYearly?'#4f46e5':'#9ca3af'}}>Yearly</span>
                                {isYearly && <Badge bg="success" style={{fontSize:10}}>Save ~20%</Badge>}
                            </div>
                        </div>
                        {/* Promo code */}
                        <Card className="border-0 shadow-sm mb-4 p-3"
                            style={{borderRadius:16,background:'linear-gradient(135deg,#fef3c7,#fff7ed)'}}>
                            <div className="fw-bold small mb-2 d-flex align-items-center gap-2">
                                <Tag size={14} color="#d97706"/>
                                <span style={{color:'#92400e'}}>Have a promo code? Apply it first!</span>
                                {promoData && <Badge bg="success" style={{fontSize:10}}>✓ Applied</Badge>}
                            </div>
                            <div className="d-flex gap-2">
                                <Form.Control
                                    placeholder="e.g. LAUNCH50, FREEDEMO"
                                    value={promoCode}
                                    onChange={e=>{
                                        setPromoCode(e.target.value.toUpperCase());
                                        if(promoData) setPromoData(null);
                                        setPromoError('');
                                    }}
                                    onKeyDown={e=>e.key==='Enter'&&applyPromo()}
                                    isInvalid={!!promoError}
                                    style={{borderRadius:10,maxWidth:280,fontSize:14,fontWeight:600,letterSpacing:1}}/>
                                <Button variant="warning" onClick={applyPromo}
                                    disabled={promoLoading||!promoCode.trim()}
                                    style={{borderRadius:10,fontWeight:600,minWidth:80}}>
                                    {promoLoading?<Spinner size="sm"/>:'Apply'}
                                </Button>
                            </div>
                            {promoError && <div className="text-danger small mt-1">{promoError}</div>}
                            {promoData && <div className="mt-2 small fw-bold" style={{color:'#059669'}}>{promoMessage}</div>}
                        </Card>
                        {/* Plan cards */}
                        {loadingInit ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" variant="primary"/>
                                <p className="text-muted small mt-2">Loading plans…</p>
                            </div>
                        ) : (
                            <Row className="g-3 mb-4">
                                {plans.map((plan, idx) => {
                                    const clr      = planColor(plan, idx);
                                    const price    = isYearly ? plan.yearly : plan.monthly;
                                    const selected = selectedPlanId === plan.id;
                                    return (
                                        <Col xs={12} sm={6} md={4} lg={plans.length >= 4 ? 3 : 4} key={plan.id}>
                                            <Card onClick={()=>setSelectedPlanId(plan.id)}
                                                style={{
                                                    borderRadius:20, cursor:'pointer', height:'100%',
                                                    border: selected?`2px solid ${clr}`:'2px solid #f1f5f9',
                                                    background: selected?`${clr}08`:'#fff',
                                                    transform: selected?'translateY(-4px)':'none',
                                                    boxShadow: selected?`0 12px 32px ${clr}25`:'0 2px 8px rgba(0,0,0,0.06)',
                                                    transition:'all 0.2s', position:'relative',
                                                }}>
                                                {plan.popular && (
                                                    <div style={{
                                                        position:'absolute',top:-12,left:'50%',
                                                        transform:'translateX(-50%)',
                                                        background:'linear-gradient(135deg,#f59e0b,#ef4444)',
                                                        color:'#fff',borderRadius:20,
                                                        padding:'4px 16px',fontSize:11,fontWeight:700,
                                                        whiteSpace:'nowrap',zIndex:1,
                                                    }}>⭐ Most Popular</div>
                                                )}
                                                <Card.Body className="p-3 p-md-4">
                                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                                        <div>
                                                            <div style={{fontSize:12,fontWeight:700,color:clr,
                                                                textTransform:'uppercase',letterSpacing:1}}>
                                                                {plan.name}
                                                            </div>
                                                            {price != null ? (
                                                                <div style={{marginTop:4}}>
                                                                    <span style={{fontSize:28,fontWeight:800}}>
                                                                        ₹{price.toLocaleString('en-IN')}
                                                                    </span>
                                                                    <span className="text-muted small">
                                                                        {isYearly ? ' /year' : (plan.is_enterprise ? ' /mo (flat)' : ' /mo')}
                                                                    </span>
                                                                    {isYearly && plan.monthly && (
                                                                        <div style={{fontSize:11,color:'#94a3b8',marginTop:2}}>
                                                                            ≈ ₹{Math.round(price/12).toLocaleString('en-IN')}/mo
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <div style={{fontSize:26,fontWeight:800,marginTop:4}}>Custom</div>
                                                            )}
                                                            {plan.max_employees && plan.max_employees < 9999 && (
                                                                <div className="text-muted" style={{fontSize:11,marginTop:2}}>
                                                                    Up to {plan.max_employees.toLocaleString('en-IN')} employees
                                                                </div>
                                                            )}
                                                            {(!plan.max_employees || plan.max_employees >= 9999) && (
                                                                <div className="text-muted" style={{fontSize:11,marginTop:2}}>
                                                                    Unlimited employees
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div style={{width:36,height:36,borderRadius:10,
                                                            background:`${clr}15`,display:'flex',
                                                            alignItems:'center',justifyContent:'center',flexShrink:0}}>
                                                            <Users size={18} color={clr}/>
                                                        </div>
                                                    </div>
                                                    {(plan.features||[]).map((f,fi)=>(
                                                        <div key={fi} className="d-flex align-items-start gap-2 mb-1">
                                                            <Check size={12} color="#059669"
                                                                style={{marginTop:2,flexShrink:0}}/>
                                                            <span style={{fontSize:12}}>{f}</span>
                                                        </div>
                                                    ))}
                                                    {selected && (
                                                        <div className="mt-3 text-center">
                                                            <Badge style={{background:clr,fontSize:11}}>✓ Selected</Badge>
                                                        </div>
                                                    )}
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>
                        )}
                        {/* Order Summary + CTA */}
                        {selectedPlan && (
                            <Card className="border-0 shadow-sm" style={{borderRadius:20}}>
                                <Card.Body className="p-4">
                                    <div className="fw-bold mb-3">Order Summary</div>
                                    <div className="d-flex justify-content-between small mb-1">
                                        <span className="text-muted">
                                            {selectedPlan.name} Plan ({isYearly?'Yearly':'Monthly'})
                                        </span>
                                        <span>
                                            {displayPrice != null
                                                ? `₹${displayPrice.toLocaleString('en-IN')}${isYearly?' /year':' /mo'}`
                                                : 'Custom'}
                                        </span>
                                    </div>
                                    {promoData && savedAmount > 0 && (
                                        <div className="d-flex justify-content-between small mb-1" style={{color:'#059669'}}>
                                            <span>Promo ({promoCode}) discount</span>
                                            <span>-₹{savedAmount.toLocaleString('en-IN')}</span>
                                        </div>
                                    )}
                                    {trialDays > 0 && (
                                        <div className="d-flex justify-content-between small mb-1" style={{color:'#059669'}}>
                                            <span>Free trial period</span>
                                            <span>{trialDays} days</span>
                                        </div>
                                    )}
                                    <hr/>
                                    <div className="d-flex justify-content-between fw-bold mb-4">
                                        <span>Amount Due Today</span>
                                        <span style={{
                                            fontSize:18,
                                            color: isEnterprise ? '#0891b2'
                                                 : skipPayment  ? '#059669' : '#1f2937',
                                        }}>
                                            {isEnterprise ? 'Contact Sales'
                                                : skipPayment ? '₹0'
                                                : `₹${finalPrice.toLocaleString('en-IN')}`}
                                        </span>
                                    </div>
                                    {isEnterprise && !skipPayment ? (
                                        <a href="mailto:sales@peoplesdock.com"
                                            className="d-block text-center text-white fw-bold py-3 rounded-pill text-decoration-none"
                                            style={{background:'linear-gradient(135deg,#0891b2,#0e7490)',fontSize:16}}>
                                            Contact Sales <ArrowRight size={18} className="ms-1"/>
                                        </a>
                                    ) : skipPayment ? (
                                        <>
                                            <Alert variant="success" className="py-2 small text-center mb-3">
                                                🎉 {trialDays > 0
                                                    ? `Your ${trialDays}-day free trial starts today — no payment needed!`
                                                    : 'No payment required! Create your account instantly.'}
                                            </Alert>
                                            <Button variant="success"
                                                className="w-100 py-3 fw-bold rounded-pill"
                                                style={{fontSize:16}}
                                                onClick={createFreeAccount}
                                                disabled={submitting}>
                                                {submitting ? <Spinner size="sm"/> : (
                                                    <span className="d-flex align-items-center justify-content-center gap-2">
                                                        Create Account Free <ArrowRight size={18}/>
                                                    </span>
                                                )}
                                            </Button>
                                        </>
                                    ) : (
                                        <div>
                                            {(gatewayCfg.gateway === 'both' || (gatewayCfg.razorpay_enabled && gatewayCfg.stripe_enabled)) && (
                                                <>
                                                    <div className="fw-bold small mb-2" style={{color:'#374151'}}>Select Payment Method</div>
                                                    <div className="d-flex gap-2 mb-3">
                                                        {[
                                                            {id:'razorpay',label:'Razorpay',sub:'UPI · Cards · NetBanking',color:'#4f46e5',logo:'RZP'},
                                                            {id:'stripe',  label:'Stripe',  sub:'International Cards',     color:'#6772e5',logo:'S'},
                                                        ].map(gw => {
                                                            const isSel = selectedGW === gw.id;
                                                            return (
                                                                <div key={gw.id}
                                                                    onClick={() => setSelectedGW(gw.id)}
                                                                    style={{
                                                                        flex:1, padding:'12px 14px', borderRadius:12,
                                                                        border: isSel ? `2px solid ${gw.color}` : '1px solid #e5e7eb',
                                                                        background: isSel ? `${gw.color}08` : '#fff',
                                                                        cursor:'pointer', display:'flex',
                                                                        alignItems:'center', gap:10,
                                                                        transition:'all 0.15s',
                                                                        boxShadow: isSel ? `0 4px 12px ${gw.color}20` : 'none',
                                                                    }}>
                                                                    <div style={{
                                                                        width:34,height:34,borderRadius:8,
                                                                        background:gw.color,color:'#fff',
                                                                        display:'flex',alignItems:'center',
                                                                        justifyContent:'center',
                                                                        fontSize:10,fontWeight:800,flexShrink:0,
                                                                    }}>
                                                                        {gw.logo}
                                                                    </div>
                                                                    <div style={{flex:1}}>
                                                                        <div style={{fontSize:13,fontWeight:700,color:'#1f2937'}}>{gw.label}</div>
                                                                        <div style={{fontSize:11,color:'#94a3b8'}}>{gw.sub}</div>
                                                                    </div>
                                                                    {isSel && <CheckCircle size={16} color={gw.color}/>}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </>
                                            )}
                                            <Button className="w-100 py-3 fw-bold rounded-pill"
                                                style={{background:'linear-gradient(135deg,#4f46e5,#7c3aed)',border:'none',fontSize:16}}
                                                onClick={selectedGW === 'stripe' ? payWithStripe : payWithRazorpay}
                                                disabled={submitting}>
                                                {submitting ? <Spinner size="sm"/> : (
                                                    <span className="d-flex align-items-center justify-content-center gap-2">
                                                        {selectedGW === 'stripe' ? '💳' : '🇮🇳'} Pay ₹{finalPrice.toLocaleString('en-IN')} & Create Account
                                                        <ArrowRight size={18}/>
                                                    </span>
                                                )}
                                            </Button>
                                        </div>
                                    )}
                                    <div className="d-flex justify-content-center align-items-center gap-3 mt-3 flex-wrap">
                                        <span className="text-muted d-flex align-items-center gap-1" style={{fontSize:11}}>
                                            <ShieldCheck size={12} color="#059669"/> 256-bit SSL
                                        </span>
                                        <span className="text-muted" style={{fontSize:11}}>•</span>
                                        <span className="text-muted" style={{fontSize:11}}>Cancel anytime</span>
                                        <span className="text-muted" style={{fontSize:11}}>•</span>
                                        <span className="text-muted" style={{fontSize:11}}>No lock-in</span>
                                    </div>
                                </Card.Body>
                            </Card>
                        )}
                        <Button variant="link"
                            className="d-block text-center mt-3 text-muted text-decoration-none"
                            onClick={()=>setStep(1)}>
                            <ChevronLeft size={14} className="me-1"/> Back to account details
                        </Button>
                    </div>
                )}
            </Container>
            {/* ── Terms & Conditions Modal ── */}
            <Modal
                show={showTermsModal}
                onHide={() => setShowTermsModal(false)}
                size="lg"
                scrollable
                centered>
                <Modal.Header
                    style={{
                        background: '#4f46e5', borderBottom: 'none',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                    <Modal.Title className="fw-bold text-white" style={{ fontSize: 18 }}>
                        📄 Terms &amp; Conditions — PeoplesDock
                    </Modal.Title>
                    <button
                        onClick={() => setShowTermsModal(false)}
                        style={{
                            background: 'transparent', border: 'none',
                            color: '#fff', fontSize: 22, cursor: 'pointer',
                            lineHeight: 1, marginLeft: 'auto', padding: '0 4px',
                        }}>×</button>
                </Modal.Header>
                <Modal.Body
                    style={{ maxHeight: '60vh', overflowY: 'auto', padding: '24px 28px' }}
                    onScroll={e => {
                        const el = e.target;
                        const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
                        if (atBottom && !termsScrolled) setTermsScrolled(true);
                    }}>
                    <div style={{ fontSize: 14, lineHeight: 1.8, color: '#374151' }}>
                        <p style={{ color: '#6b7280', fontSize: 12, marginBottom: 20 }}>
                            Last updated: June 2025 &nbsp;·&nbsp; I2Speed Technologies LLP
                        </p>
                        <h6 className="fw-bold" style={{ color: '#1f2937' }}>1. Acceptance of Terms</h6>
                        <p>By creating an account on PeoplesDock, you ("Company Admin" or "User") agree to be bound by these Terms and Conditions. These Terms apply to all users of the platform. If you do not agree to these Terms, you must not use PeoplesDock.</p>
                        <h6 className="fw-bold" style={{ color: '#1f2937' }}>2. About PeoplesDock</h6>
                        <p>PeoplesDock is a cloud-based Human Resource Management System (HRMS) developed and operated by <strong>I2Speed Technologies LLP</strong>. The platform provides HR, payroll, leave, attendance, recruitment, and performance management features on a Software-as-a-Service (SaaS) basis.</p>
                        <h6 className="fw-bold" style={{ color: '#1f2937' }}>3. Account Registration</h6>
                        <p>3.1 You must provide accurate, complete, and current information when creating your account.</p>
                        <p>3.2 You must use a valid company/work email address. Personal email providers (Gmail, Yahoo, Hotmail, etc.) are not permitted.</p>
                        <p>3.3 You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.</p>
                        <p>3.4 You must notify us immediately at support@peoplesdock.com of any unauthorized use of your account.</p>
                        <h6 className="fw-bold" style={{ color: '#1f2937' }}>4. Subscription & Billing</h6>
                        <p>4.1 <strong>Free Trial:</strong> New accounts receive a 14-day setup period to configure the platform. No payment is required during this period.</p>
                        <p>4.2 <strong>Paid Plans:</strong> After the setup period, continued access requires an active paid subscription. Pricing is as displayed on the pricing page at the time of signup.</p>
                        <p>4.3 <strong>Billing Cycles:</strong> Subscriptions are billed monthly or yearly as selected. Yearly subscriptions are billed upfront for the full year.</p>
                        <p>4.4 <strong>Refunds:</strong> Payments are non-refundable unless required by applicable law. In exceptional circumstances, refund requests may be considered at I2Speed Technologies LLP's sole discretion.</p>
                        <p>4.5 <strong>Auto-Renewal:</strong> Subscriptions auto-renew at the end of each billing cycle unless cancelled before the renewal date.</p>
                        <h6 className="fw-bold" style={{ color: '#1f2937' }}>5. Data Privacy & Security</h6>
                        <p>5.1 I2Speed Technologies LLP treats your data with strict confidentiality. Employee data, payroll information, and company records are stored securely and are not shared with third parties without your consent.</p>
                        <p>5.2 Data is encrypted at rest and in transit using industry-standard 256-bit SSL/TLS encryption.</p>
                        <p>5.3 Each company's data is fully isolated from other tenants on the platform (multi-tenant architecture).</p>
                        <p>5.4 By using PeoplesDock, you consent to the collection and processing of data as described in our Privacy Policy.</p>
                        <p>5.5 You retain ownership of all data entered into the platform. We do not claim any rights to your company or employee data.</p>
                        <h6 className="fw-bold" style={{ color: '#1f2937' }}>6. Acceptable Use</h6>
                        <p>You agree NOT to:</p>
                        <ul>
                            <li>Use the platform for any unlawful purpose or in violation of any applicable laws</li>
                            <li>Upload or transmit malicious code, viruses, or harmful content</li>
                            <li>Attempt to gain unauthorized access to any part of the platform</li>
                            <li>Resell, sublicense, or commercially exploit the platform without written permission</li>
                            <li>Use automated tools to scrape or extract data from the platform</li>
                            <li>Impersonate any person or entity or misrepresent your affiliation</li>
                        </ul>
                        <h6 className="fw-bold" style={{ color: '#1f2937' }}>7. Intellectual Property</h6>
                        <p>All intellectual property rights in PeoplesDock — including the software, design, logos, and content — belong to I2Speed Technologies LLP. You are granted a limited, non-exclusive, non-transferable licence to use the platform solely for your internal business purposes.</p>
                        <h6 className="fw-bold" style={{ color: '#1f2937' }}>8. Service Availability</h6>
                        <p>8.1 We strive to maintain 99.5% uptime but do not guarantee uninterrupted service availability.</p>
                        <p>8.2 Scheduled maintenance will be communicated in advance where possible.</p>
                        <p>8.3 We are not liable for any losses arising from service interruptions beyond our reasonable control.</p>
                        <h6 className="fw-bold" style={{ color: '#1f2937' }}>9. Limitation of Liability</h6>
                        <p>To the maximum extent permitted by law, I2Speed Technologies LLP shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of PeoplesDock. Our total liability shall not exceed the amount paid by you in the three months preceding the claim.</p>
                        <h6 className="fw-bold" style={{ color: '#1f2937' }}>10. Termination</h6>
                        <p>10.1 You may cancel your subscription at any time from your billing settings.</p>
                        <p>10.2 We reserve the right to suspend or terminate accounts that violate these Terms.</p>
                        <p>10.3 Upon termination, your data will be retained for 30 days and then permanently deleted, unless otherwise required by law.</p>
                        <h6 className="fw-bold" style={{ color: '#1f2937' }}>11. Governing Law</h6>
                        <p>These Terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of courts in India.</p>
                        <h6 className="fw-bold" style={{ color: '#1f2937' }}>12. Contact Us</h6>
                        <p>For any questions regarding these Terms, please contact us at:</p>
                        <p>
                            <strong>I2Speed Technologies LLP</strong><br/>
                            📧 support@peoplesdock.com<br/>
                            🌐 peoplesdock.com
                        </p>
                        <div style={{
                            marginTop: 24, padding: '12px 16px',
                            background: '#f0fdf4', borderRadius: 8,
                            border: '1px solid #bbf7d0',
                        }}>
                            <p style={{ margin: 0, fontSize: 13, color: '#15803d', fontWeight: 600 }}>
                                ✅ Please scroll to the bottom to confirm you have read these Terms & Conditions.
                            </p>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1px solid #e5e7eb', padding: '16px 24px' }}>
                    {!termsScrolled ? (
                        <div className="w-100 text-center">
                            <div className="text-muted small mb-2">
                                ⬇️ Please scroll down to read all Terms before accepting
                            </div>
                            <div style={{
                                height: 4, background: '#e5e7eb', borderRadius: 2, overflow: 'hidden',
                            }}>
                                <div style={{
                                    height: '100%', width: '30%',
                                    background: 'linear-gradient(90deg,#4f46e5,#7c3aed)',
                                    borderRadius: 2,
                                }}/>
                            </div>
                        </div>
                    ) : (
                        <div className="w-100 d-flex gap-2 justify-content-end">
                            <Button variant="outline-secondary"
                                style={{ borderRadius: 10 }}
                                onClick={() => {
                                    setShowTermsModal(false);
                                    setTermsAccepted(false);
                                }}>
                                Decline
                            </Button>
                            <Button
                                style={{
                                    background: 'linear-gradient(135deg,#4f46e5,#7c3aed)',
                                    border: 'none', borderRadius: 10, fontWeight: 700,
                                }}
                                onClick={() => {
                                    setTermsAccepted(true);
                                    setShowTermsModal(false);
                                    setErrors(p => ({ ...p, terms: '' }));
                                }}>
                                ✅ I Accept the Terms & Conditions
                            </Button>
                        </div>
                    )}
                </Modal.Footer>
            </Modal>
        </div>
    );
};
export default TenantSignup;
